import React from 'react';
import { FaBuilding, FaUserTie, FaCalendar, FaCheckCircle, FaTimesCircle, FaClock } from 'react-icons/fa';
import ProgressBar from '../ProgressBar/ProgressBar';
import './RequestCard.css';

const RequestCard = ({ request, showDetails = false, onViewDetails }) => {
  const getStatusIcon = () => {
    switch (request.status) {
      case 'approved':
        return <FaCheckCircle className="status-icon approved" />;
      case 'rejected':
        return <FaTimesCircle className="status-icon rejected" />;
      default:
        return <FaClock className="status-icon pending" />;
    }
  };

  // Steps come from the company's live approval chain (request.chain); only if the API
  // did not send it do we fall back to generic "Step N" placeholders.
  const getStepNames = () => {
    if (Array.isArray(request.chain) && request.chain.length > 0) {
      return request.chain.map((s) => ({ step: s.step, name: s.role_name }));
    }
    const total = Number(request.total_steps) || 0;
    return Array.from({ length: total }, (_, i) => ({
      step: i + 1,
      name: request.approvals?.find((a) => a.step === i + 1)?.role_name || `Step ${i + 1}`,
    }));
  };

  const buildSteps = () => {
    const stepNames = getStepNames();
    return stepNames.map((stepInfo, index) => {
      const approval = request.approvals?.find(a => a.step === stepInfo.step);
      const isCurrent = request.status === 'pending' && request.current_step === stepInfo.step;
      const isCompleted = approval?.action === 'approved';
      const isRejected = approval?.action === 'rejected';

      return {
        step: stepInfo.step,
        name: stepInfo.name,
        completed: isCompleted,
        current: isCurrent,
        rejected: isRejected,
      };
    });
  };

  return (
    <div className={`request-card ${request.status}`}>
      <div className="request-card-header">
        <div className="request-title-section">
          {getStatusIcon()}
          <div>
            <h3 className="request-company">{request.company}</h3>
            <p className="request-role">
              <FaUserTie /> {request.role}
              {request.previous_role && (
                <span className="previous-role-badge">
                  (Updating from {request.previous_role})
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="request-meta">
          <span className={`status-badge status-${request.status}`}>
            {request.status === 'pending' ? 'Pending' : 
             request.status === 'approved' ? 'Approved' : 'Rejected'}
          </span>
        </div>
      </div>

      <div className="request-card-body">
        <ProgressBar
          progress={request.progress}
          status={request.status}
          currentStep={request.current_step}
          currentStepName={request.current_step_info?.role_name}
          steps={buildSteps()}
        />

        {showDetails && request.approvals && request.approvals.length > 0 && (
          <div className="approval-history">
            <h4>Approval History</h4>
            <div className="approval-timeline">
              {request.approvals.map((approval, index) => (
                <div key={index} className={`approval-item ${approval.action}`}>
                  <div className="approval-step">
                    Step {approval.step}: {approval.role_name}
                  </div>
                  <div className="approval-action">
                    {approval.action === 'approved' ? (
                      <FaCheckCircle className="action-icon approved" />
                    ) : (
                      <FaTimesCircle className="action-icon rejected" />
                    )}
                    <span className={`action-text ${approval.action}`}>
                      {approval.action === 'approved' ? 'Approved' : 'Rejected'}
                    </span>
                  </div>
                  {approval.approver && (
                    <div className="approval-approver">
                      by {approval.approver}
                    </div>
                  )}
                  {approval.rejection_reason && (
                    <div className="rejection-reason">
                      Reason: {approval.rejection_reason}
                    </div>
                  )}
                  <div className="approval-date">
                    {new Date(approval.action_at).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {request.rejection_reason && (
          <div className="rejection-reason-banner">
            <FaTimesCircle />
            <div>
              <strong>Rejection Reason:</strong>
              <p>{request.rejection_reason}</p>
            </div>
          </div>
        )}

        <div className="request-dates">
          <div className="date-item">
            <FaCalendar />
            <span>Requested: {new Date(request.created_at).toLocaleDateString()}</span>
          </div>
          {request.updated_at && (
            <div className="date-item">
              <FaCalendar />
              <span>Last Updated: {new Date(request.updated_at).toLocaleDateString()}</span>
            </div>
          )}
        </div>
      </div>

      {onViewDetails && (
        <div className="request-card-footer">
          <button onClick={() => onViewDetails(request.id)} className="view-details-btn">
            View Details
          </button>
        </div>
      )}
    </div>
  );
};

export default RequestCard;
